import { useEffect, useState } from 'react'

/**
 * Sticky TOC with IntersectionObserver scrollspy.
 * Scans article body on mount, extracts all h2 elements with ids,
 * highlights active section as user scrolls.
 *
 * Usage:
 *   <TableOfContents containerSelector="#article-body" />
 *
 * Requires h2 elements in the scanned container to have `id` attributes.
 * Slugify helper provided to generate ids when wiring into templated
 * content: <h2 id={slug('Pourquoi')}>Pourquoi...</h2>
 *
 * Hidden on mobile (<lg) — TOC sidebar requires width to be useful.
 */

export function tocSlug(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60)
}

interface Heading {
  id: string
  text: string
}

interface TableOfContentsProps {
  containerSelector?: string // default 'main'
  title?: string // default 'Dans cet article'
  readingMinutes?: number
}

export function TableOfContents({
  containerSelector = 'main',
  title = 'Dans cet article',
  readingMinutes,
}: TableOfContentsProps) {
  const [headings, setHeadings] = useState<Heading[]>([])
  const [activeId, setActiveId] = useState<string>('')

  // Scan headings once mounted
  useEffect(() => {
    const container = document.querySelector(containerSelector)
    if (!container) return
    const nodes = container.querySelectorAll<HTMLElement>('h2[id]')
    const found: Heading[] = []
    nodes.forEach((n) => {
      if (n.id && n.textContent?.trim()) {
        found.push({ id: n.id, text: n.textContent.trim() })
      }
    })
    setHeadings(found)
    if (found.length > 0) setActiveId(found[0].id)
  }, [containerSelector])

  // Scrollspy via IntersectionObserver
  useEffect(() => {
    if (headings.length === 0) return
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (a.target as HTMLElement).offsetTop - (b.target as HTMLElement).offsetTop)
        if (visible.length > 0) {
          setActiveId(visible[0].target.id)
        }
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    )
    headings.forEach((h) => {
      const el = document.getElementById(h.id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [headings])

  if (headings.length < 2) return null

  return (
    <aside
      aria-label={title}
      className="pointer-events-none fixed right-4 top-24 z-10 hidden max-h-[calc(100vh-8rem)] w-64 overflow-y-auto xl:block"
    >
      <div className="pointer-events-auto rounded-xl border border-slate-200 bg-white/95 p-4 shadow-md backdrop-blur">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          {title}
        </div>
        {readingMinutes && (
          <div className="mt-1 text-[11px] text-slate-500">📖 {readingMinutes} min lecture</div>
        )}
        <nav className="mt-3 border-l-2 border-slate-100">
          <ul className="space-y-0.5">
            {headings.map((h) => (
              <li key={h.id}>
                <a
                  href={`#${h.id}`}
                  className={`block border-l-2 py-1.5 pl-3 -ml-0.5 text-xs transition-colors ${
                    activeId === h.id
                      ? 'border-primary font-semibold text-primary'
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {h.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </aside>
  )
}
